<template>
  <div
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto"
    @click.self="currentStep < 4 ? $emit('close') : null"
  >
    <div
      v-if="currentStep < 4"
      class="relative bg-[#eef1f0] w-[calc(100%-32px)] max-w-[1153px] md:h-[710px] h-[92vh] max-h-[92vh] rounded-[20px] flex flex-col md:flex-row items-center justify-center overflow-hidden mx-4 md:mx-auto"
    >
      <!-- Close button -->
      <button
        class="absolute top-3.5 right-3.5 md:top-[22px] md:right-[19px] cursor-pointer z-50 w-[25px] h-[25px]"
        @click="$emit('close')"
      >
        <svg
          width="25"
          height="25"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
          />
          <line
            x1="15"
            y1="9"
            x2="9"
            y2="15"
          />
          <line
            x1="9"
            y1="9"
            x2="15"
            y2="15"
          />
        </svg>
      </button>

      <div class="w-full md:w-[1089px] flex flex-col md:flex-row justify-between items-start gap-4 lg:gap-6 h-full md:h-[631px] md:mt-[35px] overflow-hidden p-3.5 sm:p-5 md:p-0">
        <!-- Mobile Stepper (Single column header) -->
        <div class="md:hidden w-full flex items-center justify-between px-1 pt-0.5 pb-2.5 mb-1 border-b border-black/10 shrink-0">
          <div class="flex items-center gap-1.5 sm:gap-2">
            <!-- Step 1 Circle -->
            <div
              class="w-6 h-6 sm:w-7 sm:h-7 rounded-full border-[1.5px] border-black flex items-center justify-center font-bold text-xs"
              :class="currentStep > 1 ? 'bg-black text-[#01d680]' : (currentStep === 1 ? 'bg-[#01d680] text-black font-extrabold' : 'bg-transparent text-black/40 border-black/40')"
            >
              <svg
                v-if="currentStep > 1"
                class="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span v-else>1</span>
            </div>

            <!-- Line 1-2 -->
            <div
              class="w-5 sm:w-8 h-[2px] transition-colors"
              :class="currentStep > 1 ? 'bg-black' : 'bg-black/20'"
            />

            <!-- Step 2 Circle -->
            <div
              class="w-6 h-6 sm:w-7 sm:h-7 rounded-full border-[1.5px] border-black flex items-center justify-center font-bold text-xs"
              :class="currentStep > 2 ? 'bg-black text-[#01d680]' : (currentStep === 2 ? 'bg-[#01d680] text-black font-extrabold' : 'bg-transparent text-black/40 border-black/40')"
            >
              <svg
                v-if="currentStep > 2"
                class="w-3.5 h-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <span v-else>2</span>
            </div>

            <!-- Line 2-3 -->
            <div
              class="w-5 sm:w-8 h-[2px] transition-colors"
              :class="currentStep > 2 ? 'bg-black' : 'bg-black/20'"
            />

            <!-- Step 3 Circle -->
            <div
              class="w-6 h-6 sm:w-7 sm:h-7 rounded-full border-[1.5px] border-black flex items-center justify-center font-bold text-xs"
              :class="currentStep === 3 ? 'bg-[#01d680] text-black font-extrabold' : 'bg-transparent text-black/40 border-black/40'"
            >
              <span>3</span>
            </div>
          </div>

          <!-- Step label indicator -->
          <span class="text-xs font-bold text-[#043f42] font-raleway mr-8">
            {{ currentStep === 1 ? 'Details' : (currentStep === 2 ? 'Programs' : 'Review') }}
          </span>
        </div>

        <!-- Sidebar -->
        <div class="w-[287px] h-[573px] bg-[#01d680] rounded-[20px] hidden md:flex flex-col pt-[56px] pl-[31px] flex-shrink-0">
          <div class="flex flex-col gap-0 relative">
            <!-- Step 1 -->
            <div class="flex items-start gap-[14px] z-10 bg-[#01d680]">
              <div
                class="flex-shrink-0 w-6 h-6 rounded-full border-[1.5px] border-black flex items-center justify-center transition-colors duration-300"
                :class="currentStep > 1 ? 'bg-black' : (currentStep === 1 ? 'bg-white' : 'bg-transparent')"
              >
                <svg
                  v-if="currentStep > 1"
                  class="w-3 h-3 text-[#01d680]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                ><path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3"
                  d="M5 13l4 4L19 7"
                /></svg>
                <span
                  v-else
                  class="text-black font-bold text-xs"
                  :class="{'text-white': currentStep > 1}"
                >1</span>
              </div>
              <div class="flex flex-col gap-[1px]">
                <div class="text-[15px] font-bold text-black font-raleway">
                  Cohort details
                </div>
                <div class="text-[10px] font-normal text-black font-raleway leading-tight">
                  Define cohort name, description, and training timeline.
                </div>
              </div>
            </div>

            <!-- Connecting Line 1 -->
            <div
              class="absolute left-3 top-6 w-0 h-[91px] border-l border-black z-0 transition-colors duration-300"
              :class="currentStep > 1 ? 'border-opacity-100' : 'border-opacity-30'"
            />

            <!-- Step 2 -->
            <div class="flex items-start gap-[14px] mt-[80px] z-10 bg-[#01d680]">
              <div
                class="flex-shrink-0 w-6 h-6 rounded-full border-[1.5px] border-black flex items-center justify-center transition-colors duration-300"
                :class="currentStep > 2 ? 'bg-black' : (currentStep === 2 ? 'bg-white' : 'bg-transparent border-opacity-30')"
              >
                <svg
                  v-if="currentStep > 2"
                  class="w-3 h-3 text-[#01d680]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                ><path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3"
                  d="M5 13l4 4L19 7"
                /></svg>
                <span
                  v-else
                  class="text-black font-bold text-xs"
                  :class="{'opacity-30': currentStep < 2}"
                >2</span>
              </div>
              <div
                class="flex flex-col gap-[1px]"
                :class="{'opacity-30': currentStep < 2}"
              >
                <div class="text-[15px] font-bold text-black font-raleway">
                  Manage Programs
                </div>
                <div class="text-[10px] font-normal text-black font-raleway leading-tight">
                  Add programs, enroll learners, and map contract templates.
                </div>
              </div>
            </div>

            <!-- Connecting Line 2 -->
            <div
              class="absolute left-3 top-[175px] w-0 h-[91px] border-l border-black z-0 transition-colors duration-300"
              :class="currentStep > 2 ? 'border-opacity-100' : 'border-opacity-30'"
            />

            <!-- Step 3 -->
            <div class="flex items-start gap-[14px] mt-[92px] z-10 bg-[#01d680]">
              <div
                class="flex-shrink-0 w-6 h-6 rounded-full border-[1.5px] border-black flex items-center justify-center transition-colors duration-300"
                :class="currentStep === 3 ? 'bg-white' : 'bg-transparent border-opacity-30'"
              >
                <span
                  class="text-black font-bold text-xs"
                  :class="{'opacity-30': currentStep < 3}"
                >3</span>
              </div>
              <div
                class="flex flex-col gap-[1px]"
                :class="{'opacity-30': currentStep < 3}"
              >
                <div class="text-[15px] font-bold text-black font-raleway">
                  Review Cohort details
                </div>
                <div class="text-[10px] font-normal text-black font-raleway leading-tight">
                  Verify all details and launch your new cohort.
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Content Area -->
        <div class="w-full md:w-[773px] h-full md:h-[569px] flex flex-col relative overflow-hidden flex-1 min-h-0">
          <transition
            :name="transitionName"
            mode="out-in"
          >
            <div
              :key="currentStep"
              class="flex flex-col justify-start md:justify-center w-full md:w-[773px] h-full absolute inset-0 overflow-y-auto md:overflow-visible px-2 sm:px-4 pt-1 md:p-0"
            >
              <div class="flex flex-col gap-4 sm:gap-6 md:gap-[35px] w-full md:w-[630px] h-full max-h-full">
                <div class="flex flex-col flex-shrink-0">
                  <h1 class="text-[#696969] text-[24px] sm:text-[30px] md:text-[36px] font-bold font-raleway leading-none">
                    Add a new cohort
                  </h1>
                </div>

                <!-- Step 1 Content -->
                <div
                  v-if="currentStep === 1"
                  class="flex flex-col gap-4 sm:gap-6 md:gap-[35px] flex-1 pb-16 md:pb-0"
                >
                  <div class="flex flex-col">
                    <span class="text-black text-[12px] sm:text-[13px] font-normal font-raleway">Step 1 of 3</span>
                    <h2 class="text-black text-[20px] sm:text-[24px] md:text-[29px] font-bold font-raleway">
                      Cohort details
                    </h2>
                  </div>

                  <!-- Form fields -->
                  <div class="flex flex-col gap-4 md:gap-[26px] w-full md:w-[403px]">
                    <div class="flex flex-col gap-4 md:gap-[30px]">
                      <div class="flex flex-col gap-[10px]">
                        <div class="w-full md:w-[403px] h-[45px] rounded-full border border-black bg-white flex items-center px-[20px] relative">
                          <input
                            v-model="form.name"
                            type="text"
                            class="w-full bg-transparent outline-none text-[13px] font-raleway text-black placeholder-[#5b5c60]"
                            placeholder="Cohort name"
                          >
                        </div>

                        <div class="w-full md:w-[403px] h-[45px] rounded-full border border-black bg-white flex items-center px-[20px] relative">
                          <input
                            v-model="form.location"
                            type="text"
                            class="w-full bg-transparent outline-none text-[13px] font-raleway text-black placeholder-[#5b5c60]"
                            placeholder="Cohort Location"
                          >
                        </div>

                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full md:w-[403px]">
                          <div class="flex flex-col gap-[5px]">
                            <label
                              class="w-full md:w-[209px] h-[45px] rounded-full border border-black bg-white flex items-center px-[20px] relative cursor-pointer hover:bg-[#eef1f0] transition-colors"
                              @click.prevent="openDatePicker('start')"
                            >
                              <input
                                ref="startDateInput"
                                v-model="form.startDate"
                                :type="inputTypeStart"
                                class="w-full bg-transparent outline-none text-[13px] font-raleway cursor-pointer text-black placeholder-[#5b5c60]"
                                placeholder="Start date"
                                @blur="handleDateBlur('start')"
                                @change="handleDateBlur('start')"
                                @keydown.prevent
                              >
                            </label>
                          </div>

                          <div class="flex flex-col gap-[5px]">
                            <label
                              class="w-full md:w-[177px] h-[45px] rounded-full border border-black bg-white flex items-center px-[20px] relative cursor-pointer hover:bg-[#eef1f0] transition-colors"
                              @click.prevent="openDatePicker('end')"
                            >
                              <input
                                ref="endDateInput"
                                v-model="form.endDate"
                                :type="inputTypeEnd"
                                :min="form.startDate"
                                class="w-full bg-transparent outline-none text-[13px] font-raleway cursor-pointer text-black placeholder-[#5b5c60]"
                                placeholder="End date"
                                @blur="handleDateBlur('end')"
                                @change="handleDateBlur('end')"
                                @keydown.prevent
                              >
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="flex justify-end mt-2 md:mt-[20px]">
                      <button
                        class="flex items-center justify-center w-[108px] h-[44px] rounded-full transition-colors text-[16px] font-bold font-raleway cursor-pointer"
                        :class="isStep1Valid ? 'bg-[#01d680] text-black hover:bg-[#00c270]' : 'bg-gray-300 text-gray-500 cursor-not-allowed'"
                        :disabled="!isStep1Valid"
                        @click="nextStep"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Step 2 Content (Manage Programs) -->
                <div
                  v-else-if="currentStep === 2"
                  class="flex flex-col gap-4 sm:gap-6 md:gap-[35px] flex-1 overflow-hidden h-full relative"
                >
                  <div class="flex flex-col flex-shrink-0">
                    <span class="text-black text-[12px] sm:text-[13px] font-normal font-raleway leading-tight">Step 2 of 3</span>
                    <h2 class="text-black text-[20px] sm:text-[24px] md:text-[29px] font-bold font-raleway leading-tight">
                      Manage Programs
                    </h2>
                  </div>

                  <!-- Scrollable Container -->
                  <div
                    ref="step2ScrollContainer"
                    class="flex flex-col gap-4 md:gap-[20px] w-full md:w-[630px] pr-1 md:pr-[15px] pt-2 md:pt-[24px] flex-1 overflow-y-auto no-scrollbar pb-24 md:pb-[10px] relative -mt-2 md:-mt-[24px]"
                    @scroll="checkScroll"
                  >
                    <!-- Framework Selector Pill Toggle -->
                    <div class="flex flex-col gap-1.5 w-full mb-1">
                      <div class="flex items-center justify-between text-[12px] font-raleway">
                        <span class="font-bold text-black">Accreditation Framework</span>
                        <span class="text-[11px] text-gray-500 hidden sm:inline">
                          {{ selectedFramework === 'seta' ? 'Legacy / Teach-Out Learnership (pre-July 2024)' : 'QCTO Occupational Qualification (OQSF)' }}
                        </span>
                      </div>
                      <div class="flex items-center p-1 bg-[#eef1f0] border border-black/20 rounded-full w-full">
                        <button
                          type="button"
                          class="flex-1 py-1.5 px-3 text-center text-[12px] sm:text-[13px] font-raleway font-bold rounded-full transition-all duration-200 cursor-pointer"
                          :class="selectedFramework === 'seta' ? 'bg-[#01d680] text-black shadow-xs font-extrabold' : 'text-gray-600 hover:text-black'"
                          @click="setFramework('seta')"
                        >
                          SETA (Legacy / Teach-Out)
                        </button>
                        <button
                          type="button"
                          class="flex-1 py-1.5 px-3 text-center text-[12px] sm:text-[13px] font-raleway font-bold rounded-full transition-all duration-200 cursor-pointer"
                          :class="selectedFramework === 'qcto' ? 'bg-[#01d680] text-black shadow-xs font-extrabold' : 'text-gray-600 hover:text-black'"
                          @click="setFramework('qcto')"
                        >
                          QCTO (Occupational)
                        </button>
                      </div>
                    </div>

                    <div
                      v-for="(programme, index) in form.programmes"
                      :key="index"
                      class="flex flex-col gap-[12px] w-full relative"
                    >
                      <!-- Elegant Divider -->
                      <div
                        v-if="index > 0"
                        class="w-full h-[1px] bg-gradient-to-r from-transparent via-gray-300 to-transparent mt-[8px] mb-[4px]"
                      />

                      <!-- Row 1: Dropdowns -->
                      <div
                        v-if="index === 0"
                        class="flex flex-col sm:flex-row items-stretch sm:items-start justify-between gap-3 w-full"
                      >
                        <!-- Authority / Sector Dropdown -->
                        <div class="w-full sm:w-[276px] h-[45px] rounded-full border border-black bg-white relative">
                          <CustomDropdown
                            v-model="programme.setaId"
                            :options="currentAuthorityOptions"
                            :placeholder="currentAuthorityPlaceholder"
                          />
                        </div>

                        <!-- Programme Name Dropdown -->
                        <div class="flex flex-col gap-[10px] w-full sm:w-[276px]">
                          <div class="w-full sm:w-[276px] h-[45px] rounded-full border border-black bg-white relative">
                            <CustomDropdown
                              v-model="programme.qualificationId"
                              :options="getQualificationsForBody(programme.setaId)"
                              :placeholder="currentProgrammePlaceholder"
                              :disabled="!programme.setaId"
                            />
                          </div>
                          <input
                            v-if="programme.qualificationId === 'custom'"
                            v-model="programme.customQualification"
                            type="text"
                            placeholder="Enter custom programme name"
                            class="w-full sm:w-[276px] h-[45px] rounded-full border border-black bg-white px-[16px] text-[13px] font-raleway"
                          >
                        </div>
                      </div>

                      <div
                        v-else
                        class="flex flex-col gap-[10px] items-stretch sm:items-center justify-center w-full"
                      >
                        <!-- Programme Name Dropdown (Centered) -->
                        <div class="w-full sm:w-[276px] h-[45px] rounded-full border border-black bg-white relative">
                          <CustomDropdown
                            v-model="programme.qualificationId"
                            :options="getQualificationsForBody(form.programmes[0].setaId)"
                            :placeholder="currentProgrammePlaceholder"
                            :disabled="!form.programmes[0].setaId"
                          />
                        </div>
                        <input
                          v-if="programme.qualificationId === 'custom'"
                          v-model="programme.customQualification"
                          type="text"
                          placeholder="Enter custom programme name"
                          class="w-full sm:w-[276px] h-[45px] rounded-full border border-black bg-white px-[16px] text-[13px] font-raleway"
                        >
                      </div>

                      <!-- Row 2: Upload Cards -->
                      <div class="flex flex-col sm:flex-row items-stretch justify-between gap-3 w-full">
                        <!-- Student CSV Upload -->
                        <div class="w-full sm:w-[276px] rounded-[14px] border border-black bg-transparent flex flex-col items-center justify-between py-[14px] px-[16px] gap-3 sm:gap-[20px] min-h-[195px] h-full">
                          <div class="flex flex-col items-center gap-3 sm:gap-[20px]">
                            <div class="w-[44px] h-[45px] bg-[#f8fff8] border border-[#22ec9b] rounded-lg flex items-center justify-center">
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#01d680"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              ><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line
                                x1="16"
                                y1="13"
                                x2="8"
                                y2="13"
                              /><line
                                x1="16"
                                y1="17"
                                x2="8"
                                y2="17"
                              /><polyline points="10 9 9 9 8 9" /></svg>
                            </div>

                            <div class="flex flex-col items-center gap-[4px]">
                              <span class="text-black text-[10px] font-raleway font-normal text-center leading-[16px] tracking-wide">
                                Upload a CSV containing the list of students for this program
                              </span>
                            </div>
                          </div>

                          <div class="flex flex-col items-center gap-2 w-full h-[52px]">
                            <label class="w-full h-[32px] rounded-full bg-white border border-[#22ec9b] flex items-center justify-center gap-2 cursor-pointer hover:bg-gray-50">
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="black"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              ><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line
                                x1="12"
                                y1="3"
                                x2="12"
                                y2="15"
                              /></svg>
                              <span class="text-black text-[10px] font-raleway">Upload student list</span>
                              <input
                                type="file"
                                accept=".csv,.txt"
                                class="hidden"
                                @change="e => handleCsvUpload(e, index)"
                              >
                            </label>
                            <div
                              class="flex flex-col items-center gap-1 w-full"
                              :class="{ 'h-[24px]': !programme.csvErrors?.length }"
                            >
                              <div
                                v-if="programme.csvErrors && programme.csvErrors.length > 0"
                                class="text-[10px] text-red-500 font-medium text-left w-full max-h-[100px] overflow-y-auto no-scrollbar flex flex-col gap-1 mt-1"
                              >
                                <span
                                  v-for="(err, errIdx) in programme.csvErrors"
                                  :key="errIdx"
                                >{{ err }}</span>
                              </div>
                              <span
                                v-else-if="programme.csvSummary"
                                class="text-[10px] text-[#01d680] font-bold text-center truncate w-full"
                                :title="programme.csvSummary"
                              >✅ {{ programme.csvSummary }}</span>
                              <span
                                v-else-if="programme.csvFile"
                                class="text-[10px] text-[#01d680] font-bold text-center truncate w-full"
                                :title="programme.csvFile.name"
                              >{{ programme.csvFile.name }}</span>
                              <div
                                v-else
                                class="flex items-center justify-center gap-1 cursor-pointer group mt-[2px]"
                                @click="isCsvFormatModalOpen = true"
                              >
                                <svg
                                  class="w-3 h-3 text-[#3ABFF8] group-hover:text-[#289bd1] transition-colors"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  stroke-width="2"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                >
                                  <circle
                                    cx="12"
                                    cy="12"
                                    r="10"
                                  />
                                  <line
                                    x1="12"
                                    y1="16"
                                    x2="12"
                                    y2="12"
                                  />
                                  <line
                                    x1="12"
                                    y1="8"
                                    x2="12.01"
                                    y2="8"
                                  />
                                </svg>
                                <span class="text-[10px] text-[#3ABFF8] font-medium font-raleway group-hover:text-[#289bd1] underline transition-colors">
                                  View spreadsheet requirements
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Agreement Upload -->
                        <div class="w-full sm:w-[276px] rounded-[14px] border border-black bg-transparent flex flex-col items-center justify-between py-[14px] px-[16px] gap-3 sm:gap-[20px] min-h-[195px] h-full">
                          <div class="flex flex-col items-center gap-3 sm:gap-[20px]">
                            <div class="w-[44px] h-[45px] bg-[#f8fff8] border border-[#22ec9b] rounded-lg flex items-center justify-center">
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#01d680"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              ><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line
                                x1="16"
                                y1="13"
                                x2="8"
                                y2="13"
                              /><line
                                x1="16"
                                y1="17"
                                x2="8"
                                y2="17"
                              /><polyline points="10 9 9 9 8 9" /></svg>
                            </div>

                            <div class="flex flex-col items-center gap-[4px]">
                              <span class="text-black text-[10px] font-raleway font-normal text-center leading-[16px] tracking-wide">
                                Max 10 files (5MB/file)<br>
                                Max 1000 pages per PDF<br>
                                200MB max per cohort
                              </span>
                            </div>
                          </div>

                          <div class="flex flex-col items-center gap-2 w-full h-[52px]">
                            <label class="w-[124px] h-[32px] rounded-full bg-white border border-[#22ec9b] flex items-center justify-center gap-2 cursor-pointer hover:bg-gray-50">
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="black"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              ><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line
                                x1="12"
                                y1="3"
                                x2="12"
                                y2="15"
                              /></svg>
                              <span class="text-black text-[10px] font-raleway">Upload agreement</span>
                              <input
                                type="file"
                                accept=".pdf"
                                multiple
                                class="hidden"
                                @change="e => handleAgreementUpload(e, index)"
                              >
                            </label>
                            <span
                              v-if="programme.agreementFile"
                              class="text-[10px] text-[#01d680] font-bold text-center truncate w-full h-[12px]"
                              :title="programme.agreementFile.name"
                            >{{ programme.agreementFile.name }}</span>
                            <span
                              v-else
                              class="h-[12px] w-full"
                            />
                          </div>
                        </div>
                      </div>

                      <!-- Row 3: Single Sponsor (Company & Email) -->
                      <div class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 w-full">
                        <div class="w-full sm:w-[276px] h-[45px] rounded-full border border-black bg-white flex items-center px-[20px] relative">
                          <input
                            v-model="programme.sponsors[0].companyName"
                            type="text"
                            class="w-full bg-transparent outline-none text-[13px] font-raleway text-black placeholder-[#5b5c60]"
                            placeholder="Sponsor company"
                          >
                        </div>
                        <div class="w-full sm:w-[276px] h-[45px] rounded-full border border-black bg-white flex items-center px-[20px] relative">
                          <input
                            v-model="programme.sponsors[0].email"
                            type="email"
                            class="w-full bg-transparent outline-none text-[13px] font-raleway text-black placeholder-[#5b5c60]"
                            placeholder="Sponsor Email"
                          >
                        </div>
                      </div>

                      <!-- Programme Action Row (Add / Remove Program) -->
                      <div class="flex items-center justify-between w-full h-[30px] mt-[4px]">
                        <div>
                          <button
                            v-if="index > 0"
                            type="button"
                            class="text-red-500 font-bold font-raleway text-[11px] hover:underline cursor-pointer bg-transparent border-none p-0"
                            @click="removeProgramme(index)"
                          >
                            - Remove program
                          </button>
                        </div>
                        <div>
                          <button
                            v-if="index === form.programmes.length - 1"
                            type="button"
                            class="text-black font-bold font-raleway text-[11px] flex items-center cursor-pointer hover:opacity-70 bg-transparent border-none p-0"
                            @click="addProgramme"
                          >
                            + Add another program
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-show="canScrollDown"
                    class="absolute bottom-[20px] left-1/2 transform -translate-x-1/2 animate-bounce bg-white rounded-full p-2 shadow z-20 text-[#01d680] pointer-events-none"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                </div>

                <!-- Step 3 Content -->
                <div
                  v-else-if="currentStep === 3"
                  class="flex flex-col gap-4 sm:gap-6 md:gap-[35px] flex-1 overflow-hidden"
                >
                  <div class="flex flex-col flex-shrink-0">
                    <span class="text-black text-[12px] sm:text-[13px] font-normal font-raleway leading-tight">Step 3 of 3</span>
                    <h2 class="text-black text-[20px] sm:text-[24px] md:text-[29px] font-bold font-raleway leading-tight">
                      Review cohort
                    </h2>
                  </div>

                  <div class="flex flex-col md:flex-row gap-4 md:gap-[40px] w-full md:w-[630px] flex-1 overflow-y-auto no-scrollbar pb-24 md:pb-[40px]">
                    <!-- Cohort Details Block -->
                    <div class="flex flex-col gap-2 md:gap-[15px] flex-1 bg-white/70 md:bg-transparent p-4 md:p-0 rounded-2xl md:rounded-none">
                      <h3 class="text-[15px] md:text-[16px] font-bold font-raleway text-[#696969]">
                        Cohort details
                      </h3>
                      <div class="flex flex-col gap-[7px] text-[12px] font-normal font-raleway text-[#5b5c60]">
                        <p>Cohort name: <span class="font-bold text-black">{{ form.name }}</span></p>
                        <p>Cohort location: <span class="font-bold text-black">{{ form.location }}</span></p>
                        <p>Cohort dates: <span class="font-bold text-black">{{ form.startDate }} - {{ form.endDate }}</span></p>
                      </div>
                    </div>

                    <!-- Program Details Block -->
                    <div class="flex flex-col gap-2 md:gap-[15px] flex-1 bg-white/70 md:bg-transparent p-4 md:p-0 rounded-2xl md:rounded-none">
                      <h3 class="text-[15px] md:text-[16px] font-bold font-raleway text-[#696969]">
                        Program details
                      </h3>
                      <div class="flex flex-col gap-[7px] text-[12px] font-normal font-raleway text-[#5b5c60]">
                        <p>No. of programs: <span class="font-bold text-black">{{ form.programmes.length }}</span></p>
                        <template
                          v-for="(p, i) in form.programmes"
                          :key="i"
                        >
                          <p>
                            Authority:
                            <span class="font-bold text-black">
                              {{ isQctoBody(p.setaId) ? 'QCTO (Occupational)' : 'SETA (Legacy / Teach-Out)' }}
                            </span>
                          </p>
                          <p>Program name: <span class="font-bold text-black">{{ displayQualificationName(p) }}</span></p>
                          <p v-if="p.studentCount">
                            No. of students: <span class="font-bold text-black">{{ p.studentCount }}</span>
                          </p>
                          <p>Sponsor: <span class="font-bold text-black">{{ p.sponsors[0]?.companyName }}</span></p>
                          <p>Sponsor email: <span class="font-bold text-black">{{ p.sponsors[0]?.email }}</span></p>
                          <p>Student list: <span class="font-bold text-black">Uploaded</span></p>
                          <hr
                            v-if="i < form.programmes.length - 1"
                            class="border-t border-gray-300 my-2 w-[80%]"
                          >
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- Nav Buttons -->
      <div
        v-if="currentStep === 2"
        class="absolute right-4 sm:right-[44px] bottom-3 sm:bottom-[29px] flex items-center justify-end gap-2.5 sm:gap-[20px] z-50 bg-[#eef1f0]/95 backdrop-blur-xs sm:bg-transparent p-2 sm:p-0 rounded-full sm:rounded-none shadow-sm sm:shadow-none"
      >
        <button
          class="flex items-center justify-center w-[85px] sm:w-[102px] h-[38px] sm:h-[44px] rounded-full bg-transparent border border-[#043f42] text-[#043f42] text-[14px] sm:text-[16px] font-bold font-raleway hover:bg-[#d5dad8] transition-colors cursor-pointer"
          @click="prevStep"
        >
          Back
        </button>
        <button
          class="flex items-center justify-center w-[95px] sm:w-[108px] h-[38px] sm:h-[44px] rounded-full transition-colors text-[14px] sm:text-[16px] font-bold font-raleway cursor-pointer"
          :class="isStep2Valid ? 'bg-[#01d680] text-black hover:bg-[#00c270]' : 'bg-gray-300 text-gray-500 cursor-not-allowed'"
          :disabled="!isStep2Valid"
          @click="nextStep"
        >
          Next
        </button>
      </div>

      <div
        v-else-if="currentStep === 3"
        class="absolute right-4 sm:right-[44px] bottom-3 sm:bottom-[29px] flex items-center justify-end gap-2.5 sm:gap-[20px] z-50 bg-[#eef1f0]/95 backdrop-blur-xs sm:bg-transparent p-2 sm:p-0 rounded-full sm:rounded-none shadow-sm sm:shadow-none"
      >
        <button
          class="flex items-center justify-center w-[85px] sm:w-[102px] h-[38px] sm:h-[44px] rounded-full bg-transparent border border-[#043f42] text-[#043f42] text-[14px] sm:text-[16px] font-bold font-raleway hover:bg-[#d5dad8] transition-colors cursor-pointer"
          @click="prevStep"
        >
          Back
        </button>
        <button
          class="flex items-center justify-center px-5 sm:w-[130px] h-[38px] sm:h-[44px] rounded-full transition-colors text-[14px] sm:text-[16px] font-bold font-raleway whitespace-nowrap cursor-pointer"
          :class="(isStep3Valid && !isSubmitting) ? 'bg-[#01d680] text-black hover:bg-[#00c270]' : 'bg-gray-300 text-gray-500 cursor-not-allowed'"
          :disabled="!isStep3Valid || isSubmitting"
          @click="submitCohort"
        >
          {{ isSubmitting ? 'Creating...' : 'Create cohort' }}
        </button>
      </div>
    </div>

    <!-- Step 4: Progress Modal -->
    <div
      v-else-if="currentStep === 4"
      id="progress-container"
      class="w-full flex items-center justify-center p-4"
    >
      <div
        data-pencil-id="pQV2C"
        data-pencil-name="Modal Box"
        class="box-border w-full max-w-[500px] h-fit flex flex-col gap-[24px] p-6 sm:p-[40px] justify-start items-center bg-[#FFFFFF] rounded-[20px] shadow-2xl"
      >
        <div
          data-pencil-id="VNAsD"
          data-pencil-name="Heading"
          class="text-[20px] sm:text-[24px]/[normal] box-border w-full text-[#111827] font-[Inter,system-ui,sans-serif] font-semibold text-center"
        >
          Generating Enrollment Records
        </div>
        <div
          data-pencil-id="FkTNR"
          data-pencil-name="Subheading"
          class="text-[13px] sm:text-[14px]/[21px] box-border w-full text-[#4B5563] font-[Inter,system-ui,sans-serif] font-normal text-center"
        >
          Please wait while we generate the enrollment records and prepare the agreements for your
          students. This may take a few moments.
        </div>
        <div
          data-pencil-id="fEz4U"
          data-pencil-name="Progress Bar Container"
          class="box-border w-full h-[8px] shrink-0 bg-[#E5E7EB] rounded-[4px] relative"
        >
          <div
            data-pencil-id="klGmU"
            data-pencil-name="Progress Fill"
            class="box-border h-[8px] absolute left-0 top-0 flex flex-row gap-0 justify-start items-start bg-[#043F42] rounded-[4px] [z-index:0]"
            :style="{ width: `${generationProgress}%`, transition: 'width 0.3s ease' }"
          />
        </div>
        <div
          data-pencil-id="yHqJo"
          data-pencil-name="Status"
          class="text-[14px]/[normal] box-border w-full text-[#043F42] font-[Inter,system-ui,sans-serif] font-medium text-center"
        >
          {{ generationMessage }}
        </div>
      </div>
    </div>

    <!-- CSV Format Modal -->
    <dialog
      id="csv_format_modal"
      :class="['modal', { 'modal-open': isCsvFormatModalOpen }]"
    >
      <div class="modal-box bg-white border border-[#22ec9b] rounded-[20px] p-5 sm:p-8 relative w-[calc(100%-32px)] max-w-lg mx-4 sm:mx-auto shadow-[0_8px_30px_rgb(0,0,0,0.12)]">
        <h3 class="font-bold text-[18px] sm:text-[22px] text-black font-raleway mb-2">
          Student List Formatting Requirements
        </h3>
        <p class="py-2 text-[13px] sm:text-[14px] text-gray-600 font-raleway leading-relaxed">
          To ensure your cohort is registered accurately, please arrange your spreadsheet exactly in the order below. Doccu processes the data by column position.
        </p>

        <div class="flex flex-col gap-3 sm:gap-4 mt-4 sm:mt-6 mb-4">
          <div class="flex flex-col bg-[#f8fff8] rounded-[14px] p-3 sm:p-4 border border-[#22ec9b]/30">
            <span class="text-[10px] sm:text-[11px] font-bold text-[#01d680] uppercase tracking-wider mb-1">Column 1 (Required)</span>
            <span class="text-[14px] sm:text-[15px] text-black font-raleway font-semibold">Student Name</span>
          </div>
          <div class="flex flex-col bg-[#f8fff8] rounded-[14px] p-3 sm:p-4 border border-[#22ec9b]/30">
            <span class="text-[10px] sm:text-[11px] font-bold text-[#01d680] uppercase tracking-wider mb-1">Column 2 (Required)</span>
            <span class="text-[14px] sm:text-[15px] text-black font-raleway font-semibold">Student Surname</span>
          </div>
          <div class="flex flex-col bg-[#f8fff8] rounded-[14px] p-3 sm:p-4 border border-[#22ec9b]/30">
            <span class="text-[10px] sm:text-[11px] font-bold text-[#01d680] uppercase tracking-wider mb-1">Column 3 (Required)</span>
            <span class="text-[14px] sm:text-[15px] text-black font-raleway font-semibold">Email Address</span>
          </div>
          <div class="flex flex-col bg-gray-50 rounded-[14px] p-3 sm:p-4 border border-gray-200">
            <span class="text-[10px] sm:text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">Column 4 (Optional)</span>
            <span class="text-[14px] sm:text-[15px] text-gray-700 font-raleway font-medium">Cell Number</span>
          </div>
        </div>

        <div class="modal-action mt-6 sm:mt-8 flex justify-end">
          <button
            class="flex items-center justify-center h-[40px] sm:h-[44px] px-6 sm:px-8 rounded-full bg-[#043f42] hover:bg-[#032a2c] text-white font-bold font-raleway transition-colors border-none cursor-pointer"
            @click="isCsvFormatModalOpen = false"
          >
            Understood
          </button>
        </div>
      </div>
      <form
        method="dialog"
        class="modal-backdrop bg-black/40"
        @click="isCsvFormatModalOpen = false"
      >
        <button>close</button>
      </form>
    </dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import CustomDropdown from './custom_dropdown.vue'
import setaQualificationsData from '../data/setas_and_qualifications.json'

const props = defineProps({
  setaQualifications: {
    type: [Object, String],
    default: () => ({ setas: [] })
  }
})

const emit = defineEmits(['close', 'next'])

const currentStep = ref(1)
const step2ScrollContainer = ref(null)
const canScrollDown = ref(false)
const transitionName = ref('slide-right')
const generationProgress = ref(0)
const generationMessage = ref('Initializing generation...')
const isSubmitting = ref(false)

const isCsvFormatModalOpen = ref(false)

if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  window.__setCohortWizardStep = (step) => { currentStep.value = step }
  window.__getCohortWizardForm = () => form.value
}

const startDateInput = ref(null)
const endDateInput = ref(null)
const inputTypeStart = ref('text')
const inputTypeEnd = ref('text')

const openDatePicker = async (field) => {
  if (field === 'start') {
    inputTypeStart.value = 'date'
    await nextTick()
    if (startDateInput.value) {
      startDateInput.value.focus()
      try { startDateInput.value.showPicker() } catch (e) {}
    }
  } else {
    inputTypeEnd.value = 'date'
    await nextTick()
    if (endDateInput.value) {
      endDateInput.value.focus()
      try { endDateInput.value.showPicker() } catch (e) {}
    }
  }
}

const handleDateBlur = (field) => {
  if (field === 'start') {
    if (!form.value.startDate) inputTypeStart.value = 'text'
  } else {
    if (!form.value.endDate) inputTypeEnd.value = 'text'
  }
}

// Use authoritative static data with props fallback for SETAs and QCTO qualifications

const rawQualificationsData = computed(() => {
  let data = props.setaQualifications
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch (e) {
      data = null
    }
  }
  if (data && ((data.setas && data.setas.length > 0) || (data.qcto_clusters && data.qcto_clusters.length > 0))) {
    return data
  }
  return setaQualificationsData || { setas: [], qcto_clusters: [] }
})

const parsedSetas = computed(() => {
  return (rawQualificationsData.value?.setas || []).map(seta => {
    return {
      ...seta,
      name: seta.short_name || (seta.name ? seta.name.split(' (')[0] : seta.id),
      fullName: seta.name || seta.id
    }
  })
})

const parsedQctoClusters = computed(() => {
  return (rawQualificationsData.value?.qcto_clusters || []).map(cluster => {
    return {
      ...cluster,
      name: cluster.short_name || cluster.name,
      fullName: cluster.name
    }
  })
})

const selectedFramework = ref('seta')

const setFramework = (framework) => {
  if (selectedFramework.value === framework) return
  selectedFramework.value = framework
  form.value.programmes.forEach(p => {
    p.setaId = ''
    p.qualificationId = ''
    p.customQualification = ''
  })
}

const isQctoBody = (bodyId) => {
  if (!bodyId) return false
  return bodyId.startsWith('qcto') || parsedQctoClusters.value.some(c => c.id === bodyId)
}

const currentAuthorityOptions = computed(() => {
  return selectedFramework.value === 'seta' ? parsedSetas.value : parsedQctoClusters.value
})

const currentAuthorityPlaceholder = computed(() => {
  return selectedFramework.value === 'seta' ? 'Select SETA Type' : 'Select Occupational Sector'
})

const currentProgrammePlaceholder = computed(() => {
  return selectedFramework.value === 'seta' ? 'Select Program name' : 'Select Qualification'
})

const getQualificationsForBody = (bodyId) => {
  if (!bodyId) return []
  const allBodies = [...parsedSetas.value, ...parsedQctoClusters.value]
  const body = allBodies.find(b => b.id === bodyId)
  const baseQuals = (body?.qualifications || []).map(q => {
    const metaParts = []
    if (q.nqf_level) metaParts.push(`NQF ${q.nqf_level}`)
    if (q.saqa_id) metaParts.push(`SAQA ${q.saqa_id}`)
    const metaStr = metaParts.length > 0 ? ` (${metaParts.join(' · ')})` : ''
    return {
      id: q.id,
      name: `${q.name}${metaStr}`,
      fullName: q.name,
      rawName: q.name,
      saqaId: q.saqa_id,
      nqfLevel: q.nqf_level
    }
  })
  baseQuals.push({ id: 'custom', name: '+ Add Custom Programme', fullName: '+ Add Custom Programme' })
  return baseQuals
}

const displayQualificationName = (p) => {
  if (p.qualificationId === 'custom') return p.customQualification
  const quals = getQualificationsForBody(p.setaId)
  const found = quals.find(q => q.id === p.qualificationId)
  if (found) return found.name
  const allQuals = [...parsedSetas.value, ...parsedQctoClusters.value].flatMap(b => b.qualifications || [])
  const globalFound = allQuals.find(q => q.id === p.qualificationId)
  return globalFound ? globalFound.name : p.qualificationId
}

const checkScroll = () => {
  if (!step2ScrollContainer.value) return
  const el = step2ScrollContainer.value
  canScrollDown.value = el.scrollHeight > el.clientHeight && Math.ceil(el.scrollTop) < el.scrollHeight - el.clientHeight - 1
}

const form = ref({
  name: '',
  location: '',
  startDate: '',
  endDate: '',
  programmes: [
    {
      setaId: '',
      qualificationId: '',
      customQualification: '',
      csvFile: null,
      csvErrors: null,
      csvSummary: null,
      agreementFile: null,
      agreementFiles: [],
      sponsors: [
        { email: '', companyName: '' }
      ]
    }
  ]
})

watch(() => form.value.programmes, () => {
  if (currentStep.value === 2) {
    nextTick(() => {
      checkScroll()
    })
  }
}, { deep: true })

watch(() => form.value.programmes[0]?.setaId, (newSetaId) => {
  if (newSetaId) {
    if (isQctoBody(newSetaId) && selectedFramework.value !== 'qcto') {
      selectedFramework.value = 'qcto'
    } else if (!isQctoBody(newSetaId) && selectedFramework.value !== 'seta') {
      selectedFramework.value = 'seta'
    }
  }
  if (newSetaId !== undefined) {
    form.value.programmes.forEach((p, i) => {
      if (i > 0 && p.setaId !== newSetaId) {
        p.setaId = newSetaId
        p.qualificationId = ''
      }
    })
  }
})

const isStep1Valid = computed(() => {
  return form.value.name.trim() !== '' &&
         form.value.location.trim() !== '' &&
         form.value.startDate !== '' &&
         form.value.endDate !== '' &&
         new Date(form.value.startDate) <= new Date(form.value.endDate)
})

const isStep2Valid = computed(() => {
  if (form.value.programmes.length === 0) return false

  return form.value.programmes.every(p => {
    const hasBasic = p.setaId && p.setaId.trim() !== '' &&
                     p.qualificationId && p.qualificationId.trim() !== '' &&
                     (p.qualificationId !== 'custom' || (p.customQualification && p.customQualification.trim() !== '')) &&
                     p.csvFile !== null &&
                     !p.csvErrors && // MUST NOT have CSV errors
                     p.csvSummary !== null && // MUST have successful validation summary
                     p.agreementFiles && p.agreementFiles.length > 0

    if (!hasBasic) return false

    if (!p.sponsors || p.sponsors.length === 0) return false

    const sponsor = p.sponsors[0]
    return sponsor &&
           sponsor.companyName && sponsor.companyName.trim() !== '' &&
           sponsor.email && sponsor.email.trim() !== ''
  })
})

const isStep3Valid = computed(() => {
  return isStep1Valid.value && isStep2Valid.value
})

const handleCsvUpload = async (e, index) => {
  if (form.value.programmes[index].isValidating) return

  if (e.target.files && e.target.files.length > 0) {
    const file = e.target.files[0]
    if (!file.name.toLowerCase().match(/\.(csv|txt)$/)) {
      window.showFlash('error', 'Only CSV or text files are allowed')
      return
    }
    form.value.programmes[index].csvFile = file
    form.value.programmes[index].csvErrors = null
    form.value.programmes[index].csvSummary = null
    form.value.programmes[index].isValidating = true

    const formData = new FormData()
    formData.append('student_csv', file)

    try {
      const response = await fetch('/cohorts/validate_csv', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: formData
      })

      const data = await response.json()

      if (!response.ok || data.errors) {
        const errors = data.errors || ['Validation failed']
        form.value.programmes[index].csvErrors = errors
        form.value.programmes[index].csvFile = null
      } else {
        form.value.programmes[index].csvSummary = `${data.total_students} students uploaded`
        form.value.programmes[index].studentCount = data.total_students
      }
    } catch (error) {
      const err = ['An error occurred during validation']
      form.value.programmes[index].csvErrors = err
      form.value.programmes[index].csvFile = null
    } finally {
      form.value.programmes[index].isValidating = false
    }
  } else {
    // Handle cancellation: clear state
    form.value.programmes[index].csvFile = null
    form.value.programmes[index].csvErrors = null
    form.value.programmes[index].csvSummary = null
  }
}

const handleAgreementUpload = (e, index) => {
  if (e.target.files && e.target.files.length > 0) {
    const files = Array.from(e.target.files)
    const existingFiles = form.value.programmes[index].agreementFiles || []
    if (existingFiles.length + files.length > 10) {
      window.showFlash('error', 'Max 10 files allowed')
      return
    }
    const oversized = files.find(f => f.size > 5 * 1024 * 1024)
    if (oversized) {
      window.showFlash('error', 'Max 5MB per file')
      return
    }
    form.value.programmes[index].agreementFile = files[files.length - 1] // Kept for display purposes
    form.value.programmes[index].agreementFiles = [...existingFiles, ...files]
    nextTick(() => checkScroll())
  }
}

const addProgramme = () => {
  form.value.programmes.push({
    setaId: form.value.programmes[0]?.setaId || '',
    qualificationId: '',
    customQualification: '',
    csvFile: null,
    csvErrors: null,
    csvSummary: null,
    agreementFile: null,
    agreementFiles: [],
    sponsors: [{ email: '', companyName: '' }]
  })
  nextTick(() => checkScroll())
  // Scroll to bottom of container
  setTimeout(() => {
    if (step2ScrollContainer.value) step2ScrollContainer.value.scrollTop = step2ScrollContainer.value.scrollHeight
  }, 100)
}

const removeProgramme = (index) => {
  form.value.programmes.splice(index, 1)
  nextTick(() => checkScroll())
}

const nextStep = () => {
  if (currentStep.value === 1 && !isStep1Valid.value) return
  if (currentStep.value === 2 && !isStep2Valid.value) return
  if (currentStep.value < 3) {
    transitionName.value = 'slide-right'
    currentStep.value++
  }
}

const prevStep = () => {
  if (currentStep.value > 1) {
    transitionName.value = 'slide-left'
    currentStep.value--
  }
}

const submitCohort = async () => {
  if (!isStep3Valid.value) return
  isSubmitting.value = true

  const formData = new FormData()
  formData.append('cohort[name]', form.value.name)
  formData.append('cohort[location]', form.value.location)
  formData.append('cohort[start_date]', form.value.startDate)
  formData.append('cohort[end_date]', form.value.endDate)

  form.value.programmes.forEach((prog, idx) => {
    formData.append(`cohort[programmes_attributes][${idx}][seta_type]`, prog.setaId)
    const qualId = prog.qualificationId === 'custom' ? prog.customQualification : prog.qualificationId
    formData.append(`cohort[programmes_attributes][${idx}][qualification_id]`, qualId)

    if (prog.csvFile) {
      formData.append(`cohort[programmes_attributes][${idx}][student_csv]`, prog.csvFile)
      if (prog.studentCount) {
        formData.append(`cohort[programmes_attributes][${idx}][students_count]`, prog.studentCount)
      }
    }
    if (prog.agreementFiles && prog.agreementFiles.length > 0) {
      prog.agreementFiles.forEach(file => {
        formData.append(`cohort[programmes_attributes][${idx}][agreements][]`, file)
      })
    }

    prog.sponsors.forEach((sponsor, sIdx) => {
      formData.append(`cohort[programmes_attributes][${idx}][sponsors_attributes][${sIdx}][name]`, sponsor.companyName)
      formData.append(`cohort[programmes_attributes][${idx}][sponsors_attributes][${sIdx}][email]`, sponsor.email)
      formData.append(`cohort[programmes_attributes][${idx}][sponsors_attributes][${sIdx}][company_name]`, sponsor.companyName)
    })
  })

  try {
    currentStep.value = 4
    generationProgress.value = 10
    generationMessage.value = 'Uploading agreement and student records...'

    const response = await fetch('/cohorts', {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
      },
      body: formData
    })

    if (response.ok) {
      const data = await response.json()
      generationProgress.value = 25
      generationMessage.value = 'Preparing agreement documents...'

      const cohortId = data.id
      let pollTimer = null

      const pollStatus = async () => {
        try {
          const statusRes = await fetch(`/cohorts/${cohortId}/generation_status.json`)
          if (statusRes.ok) {
            const statusData = await statusRes.json()
            if (statusData.progress !== undefined && statusData.progress > generationProgress.value) {
              generationProgress.value = statusData.progress
            }
            if (statusData.message) {
              generationMessage.value = statusData.message
            }

            if (statusData.redirect_url) {
              if (pollTimer) clearInterval(pollTimer)
              generationProgress.value = 100
              generationMessage.value = 'Redirecting to Mapping Studio...'
              emit('close')
              window.location.href = statusData.redirect_url
            } else if (statusData.error) {
              if (pollTimer) clearInterval(pollTimer)
              window.showFlash('error', 'Generation failed: ' + statusData.error)
              currentStep.value = 3
              isSubmitting.value = false
            }
          }
        } catch (e) {
          console.error('Polling error:', e)
        }
      }

      await pollStatus()
      pollTimer = setInterval(pollStatus, 400)
    } else {
      currentStep.value = 3
      const err = await response.json()
      console.error('Error creating cohort:', err)
      window.showFlash('error', 'Error creating cohort: ' + (err.error || 'Unknown error'))
    }
  } catch (error) {
    currentStep.value = 3
    console.error('Failed to connect to server:', error)
    window.showFlash('error', 'Failed to connect to server')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
/* Hide scrollbar for Chrome, Safari and Opera */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.no-scrollbar {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
/* Horizontal slide transitions (300ms ease-in-out) */
.slide-right-enter-active,
.slide-right-leave-active,
.slide-left-enter-active,
.slide-left-leave-active {
  transition: all 0.3s ease-in-out;
}

/* Slide Right (Next) */
.slide-right-enter-from {
  opacity: 0;
  transform: translateX(50px);
}
.slide-right-leave-to {
  opacity: 0;
  transform: translateX(-50px);
}

/* Slide Left (Back) */
.slide-left-enter-from {
  opacity: 0;
  transform: translateX(-50px);
}
.slide-left-leave-to {
  opacity: 0;
  transform: translateX(50px);
}
</style>
